import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image,
  Loader2,
  MessageCirclePlus,
  Paperclip,
  Send,
} from 'lucide-react';
import { Badge, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import { EmojiPicker } from '@/components/support/EmojiPicker';
import { HelpSidebar } from '@/components/support/HelpSidebar';
import {
  useArchivedConversation,
  useArchivedConversations,
  useConversation,
  useCreateAttachmentUploadTicket,
  useMarkConversationRead,
  useMessageAttachmentDownloadUrl,
  useSendMessage,
  useStartNewConversation,
} from '@/api/queries/support';
import type { MessageResponse } from '@/api/queries/support';
import { useConversationRealtime } from '@/features/messages/useConversationRealtime';
import { formatBytes } from '@/lib/format';

/** Types de pièce jointe acceptés côté client — le serveur revalide le fichier réel. */
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;

const timeFormatter = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const dayFormatter = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Clé de regroupement par jour civil (heure locale), indépendante de l'heure du message. */
function dayKey(value: string): string {
  const date = new Date(value);

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** « Aujourd'hui », « Hier », ou la date complète pour tout le reste. */
function dayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (dayKey(value) === dayKey(today.toISOString())) {
    return "Aujourd'hui";
  }

  if (dayKey(value) === dayKey(yesterday.toISOString())) {
    return 'Hier';
  }

  return dayFormatter.format(date);
}

function DateDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-2 text-xs font-medium text-ink-400">
      <span className="h-px flex-1 bg-ink-100" />
      {label}
      <span className="h-px flex-1 bg-ink-100" />
    </div>
  );
}

function AttachmentChip({ message }: { message: MessageResponse }) {
  const downloadUrl = useMessageAttachmentDownloadUrl();
  const isImage = (message.attachmentContentType ?? '').startsWith('image/');
  const Icon = isImage ? Image : FileText;

  function handleClick() {
    if (downloadUrl.isPending) {
      return;
    }

    downloadUrl.mutate(message.id, {
      onSuccess: (result) => {
        window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
      },
    });
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={downloadUrl.isPending}
        className="flex items-center gap-2 rounded-input border border-ink-100 bg-white px-3 py-2 text-left text-xs text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
      >
        {downloadUrl.isPending ? (
          <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-ink-400" />
        ) : (
          <Icon aria-hidden className="size-4 shrink-0 text-ink-400" />
        )}
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{message.attachmentFileName ?? 'Pièce jointe'}</span>
          {message.attachmentSizeBytes != null ? (
            <span className="text-[11px] text-ink-400">{formatBytes(message.attachmentSizeBytes)}</span>
          ) : null}
        </span>
      </button>

      {downloadUrl.isError ? (
        <span className="px-1 text-[11px] text-danger-500">Impossible d'ouvrir la pièce jointe.</span>
      ) : null}
    </div>
  );
}

function MessageBubble({ message }: { message: MessageResponse }) {
  const isMine = message.isMine;
  const isRead = Boolean(message.readByAdminAt);

  return (
    <div className={`flex items-end gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isMine ? (
        <span
          aria-hidden
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[11px] font-semibold text-ink-700"
        >
          SC
        </span>
      ) : null}

      <div className={`flex max-w-[75%] flex-col gap-1 ${isMine ? 'items-end' : 'items-start'}`}>
        {!isMine ? <span className="px-1 text-xs font-medium text-ink-500">Service client</span> : null}

        {message.hasAttachment ? <AttachmentChip message={message} /> : null}

        {message.body ? (
          <div
            className={`rounded-card px-4 py-2.5 text-sm ${
              isMine
                ? 'bg-brand-500 text-white'
                : 'border border-ink-100 bg-white text-ink-900 shadow-card'
            }`}
          >
            {message.body}
          </div>
        ) : null}

        <span className="flex items-center gap-1 px-1 text-[11px] text-ink-400">
          {timeFormatter.format(new Date(message.createdAt))}
          {isMine ? (
            isRead ? (
              <CheckCheck aria-label="Lu" className="size-3.5 text-brand-500" />
            ) : (
              <Check aria-label="Envoyé" className="size-3.5" />
            )
          ) : null}
        </span>
      </div>
    </div>
  );
}

const ARCHIVE_PAGE_SIZE = 10;

/** Liste paginée des conversations support fermées (archive) du tenant courant. */
function ArchiveList({ onOpen }: { onOpen: (conversationId: string) => void }) {
  const [page, setPage] = useState(1);
  const archive = useArchivedConversations(page, ARCHIVE_PAGE_SIZE);
  const result = archive.data;

  return (
    <div className="flex flex-col gap-1 px-5 py-5">
      {archive.isPending ? <LoadingState label="Chargement des archives…" /> : null}

      {archive.isError ? (
        <ErrorState message="Impossible de charger les archives pour le moment." onRetry={() => void archive.refetch()} />
      ) : null}

      {archive.isSuccess && result!.items.length === 0 ? (
        <EmptyState
          title="Aucune conversation archivée"
          description="Les conversations fermées apparaîtront ici."
        />
      ) : null}

      {archive.isSuccess && result!.items.length > 0 ? (
        <>
          <ul className="flex flex-col gap-2">
            {result!.items.map((item) => (
              <li key={item.conversationId}>
                <button
                  type="button"
                  onClick={() => onOpen(item.conversationId)}
                  className="flex w-full flex-col gap-1 rounded-card border border-ink-100 bg-white px-4 py-3 text-left transition hover:bg-ink-50"
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-ink-500">
                      {item.closedAt ? `Fermée le ${new Date(item.closedAt).toLocaleDateString('fr-FR')}` : 'Fermée'}
                    </span>
                  </span>
                  <span className="truncate text-sm text-ink-900">
                    {item.lastMessagePreview || 'Aucun message'}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between pt-3">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={!result!.hasPreviousPage}
              className="flex items-center gap-1 rounded-pill border border-ink-100 px-3 py-1.5 text-xs font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-40"
            >
              <ChevronLeft className="size-3.5" />
              Précédent
            </button>

            <span className="text-xs text-ink-400">
              Page {result!.page} / {Math.max(1, result!.totalPages)}
            </span>

            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={!result!.hasNextPage}
              className="flex items-center gap-1 rounded-pill border border-ink-100 px-3 py-1.5 text-xs font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-40"
            >
              Suivant
              <ChevronRight className="size-3.5" />
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Détail en lecture seule d'une conversation archivée. */
function ArchiveDetail({ conversationId, onBack }: { conversationId: string; onBack: () => void }) {
  const conversation = useArchivedConversation(conversationId);
  const messages = conversation.data?.messages ?? [];
  let lastDayKey = '';

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-ink-100/70 px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-medium text-ink-500 transition hover:text-ink-900"
        >
          <ArrowLeft className="size-3.5" />
          Retour aux archives
        </button>

        <span
          title={conversationId}
          className="shrink-0 rounded-pill bg-ink-50 px-2.5 py-1 font-mono text-[11px] text-ink-400"
        >
          #{conversationId.slice(0, 8)}
        </span>
      </div>

      <div className="flex max-h-[28rem] min-h-[16rem] flex-col gap-1 overflow-y-auto bg-ink-50/40 px-5 py-5">
        {conversation.isPending ? <LoadingState label="Chargement de la conversation…" /> : null}

        {conversation.isError ? (
          <ErrorState
            message="Impossible de charger cette conversation pour le moment."
            onRetry={() => void conversation.refetch()}
          />
        ) : null}

        {conversation.isSuccess && messages.length === 0 ? (
          <EmptyState title="Aucun message" description="Cette conversation ne contient aucun message." />
        ) : null}

        {messages.map((message) => {
          const key = dayKey(message.createdAt);
          const showDivider = key !== lastDayKey;
          lastDayKey = key;

          return (
            <div key={message.id} className="flex flex-col gap-1">
              {showDivider ? <DateDivider label={dayLabel(message.createdAt)} /> : null}
              <MessageBubble message={message} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Messages : chat avec le support Pixlide.
 *
 * Une conversation par tenant, créée paresseusement au premier message envoyé — la requête
 * `GET /api/v1/support` renvoie donc `conversationId: null` tant que personne n'a jamais
 * écrit, ce qui n'est pas une erreur mais l'état de départ normal.
 */
export function MessagesPage() {
  const conversation = useConversation();
  const sendMessage = useSendMessage();
  const markRead = useMarkConversationRead();
  const startNewConversation = useStartNewConversation();
  const uploadTicket = useCreateAttachmentUploadTicket();
  const [draft, setDraft] = useState('');
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<'active' | 'archive'>('active');
  const [selectedArchiveId, setSelectedArchiveId] = useState<string | null>(null);

  useConversationRealtime();

  const messages = conversation.data?.messages ?? [];
  const isClosed = conversation.data?.isClosed ?? false;

  // Marque la conversation lue dès qu'elle est affichée avec des messages non lus par le
  // client. Pas de dépendance sur `markRead` (identité de mutation instable) pour éviter une
  // boucle de déclenchements.
  useEffect(() => {
    if (conversation.data?.conversationId && messages.some((message) => !message.isMine && !message.readByClientAt)) {
      markRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.data?.conversationId, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  function handleSend() {
    const body = draft.trim();

    if (!body || sendMessage.isPending) {
      return;
    }

    setDraft('');
    sendMessage.mutate({ body });
  }

  function handleAttachmentClick() {
    if (isUploadingAttachment) {
      return;
    }

    setAttachmentError(null);
    fileInputRef.current?.click();
  }

  async function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setAttachmentError(null);

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      setAttachmentError('Type de fichier non pris en charge (images ou PDF uniquement).');
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setAttachmentError('Le fichier dépasse la taille maximale autorisée (25 Mo).');
      return;
    }

    setIsUploadingAttachment(true);

    try {
      const ticket = await uploadTicket.mutateAsync({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      const uploadResponse = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: ticket.requiredHeaders,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Échec du dépôt du fichier (${uploadResponse.status})`);
      }

      const body = draft.trim();
      setDraft('');

      await sendMessage.mutateAsync({
        body: body || null,
        attachmentStorageKey: ticket.storageKey,
        attachmentFileName: file.name,
        attachmentContentType: file.type,
        attachmentSizeBytes: file.size,
      });
    } catch {
      setAttachmentError("Impossible d'envoyer la pièce jointe. Réessayez.");
    } finally {
      setIsUploadingAttachment(false);
    }
  }

  let lastDayKey = '';

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader title="Messages" />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-3">
          <div className="flex w-fit gap-1 rounded-pill bg-ink-50 p-1">
            <button
              type="button"
              onClick={() => setTab('active')}
              className={`rounded-pill px-4 py-1.5 text-sm font-medium transition ${
                tab === 'active' ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900'
              }`}
            >
              Conversation
            </button>
            <button
              type="button"
              onClick={() => setTab('archive')}
              className={`rounded-pill px-4 py-1.5 text-sm font-medium transition ${
                tab === 'archive' ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900'
              }`}
            >
              Archives
            </button>
          </div>

          {tab === 'archive' ? (
            <Card className="flex flex-col overflow-hidden">
              {selectedArchiveId ? (
                <ArchiveDetail conversationId={selectedArchiveId} onBack={() => setSelectedArchiveId(null)} />
              ) : (
                <ArchiveList onOpen={setSelectedArchiveId} />
              )}
            </Card>
          ) : (
        <Card className="flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-ink-100/70 px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="relative flex size-10 items-center justify-center rounded-full bg-ink-100 text-sm font-semibold text-ink-700">
                SC
                <span
                  aria-hidden
                  className="absolute right-0 bottom-0 size-2.5 rounded-full border-2 border-white bg-success-500"
                />
              </span>
              <div>
                <p className="font-semibold text-ink-900">Service Client</p>
                <p className="text-xs text-ink-500">En ligne</p>
              </div>
            </div>

            {conversation.data?.conversationId ? (
              <span
                title={conversation.data.conversationId}
                className="shrink-0 rounded-pill bg-ink-50 px-2.5 py-1 font-mono text-[11px] text-ink-400"
              >
                #{conversation.data.conversationId.slice(0, 8)}
              </span>
            ) : null}
          </div>

          <div
            ref={scrollRef}
            className="flex max-h-[28rem] min-h-[16rem] flex-col gap-1 overflow-y-auto bg-ink-50/40 px-5 py-5"
          >
            {conversation.isPending ? <LoadingState label="Chargement de la conversation…" /> : null}

            {conversation.isError ? (
              <ErrorState
                message="Impossible de charger la conversation pour le moment."
                onRetry={() => void conversation.refetch()}
              />
            ) : null}

            {conversation.isSuccess && messages.length === 0 ? (
              <EmptyState
                title="Aucun message pour l'instant"
                description="Écrivez-nous ci-dessous, notre équipe vous répond généralement en quelques minutes."
              />
            ) : null}

            {messages.map((message) => {
              const key = dayKey(message.createdAt);
              const showDivider = key !== lastDayKey;
              lastDayKey = key;

              return (
                <div key={message.id} className="flex flex-col gap-1">
                  {showDivider ? <DateDivider label={dayLabel(message.createdAt)} /> : null}
                  <MessageBubble message={message} />
                </div>
              );
            })}
          </div>

          {attachmentError ? (
            <p className="border-t border-ink-100/70 px-4 pt-3 text-xs text-danger-500">{attachmentError}</p>
          ) : null}

          {isClosed ? (
            <div className="flex flex-col items-center gap-3 border-t border-ink-100/70 px-4 py-5 text-center">
              <Badge tone="neutral">Conversation fermée</Badge>
              <p className="text-sm text-ink-500">
                Cette conversation est fermée. Démarrez une nouvelle conversation pour continuer à échanger avec le
                support.
              </p>

              <button
                type="button"
                onClick={() => startNewConversation.mutate()}
                disabled={startNewConversation.isPending}
                className="flex items-center gap-1.5 rounded-pill bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
              >
                {startNewConversation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <MessageCirclePlus className="size-4" />
                )}
                Nouvelle conversation
              </button>

              {startNewConversation.isError ? (
                <p className="text-xs text-danger-500">Impossible de démarrer une nouvelle conversation. Réessayez.</p>
              ) : null}
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleSend();
              }}
              className={`flex items-center gap-2 px-4 py-3 ${attachmentError ? '' : 'border-t border-ink-100/70'}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                className="hidden"
                onChange={(event) => void handleFileSelected(event)}
              />

              <button
                type="button"
                aria-label="Joindre un fichier"
                onClick={handleAttachmentClick}
                disabled={isUploadingAttachment}
                className="text-ink-400 hover:text-ink-700 disabled:opacity-50"
              >
                {isUploadingAttachment ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Paperclip className="size-5" />
                )}
              </button>
              <EmojiPicker onSelect={(emoji) => setDraft((current) => `${current}${emoji}`)} />

              <input
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Écrivez votre message…"
                className="flex-1 rounded-pill bg-ink-50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-200"
              />

              <button
                type="submit"
                aria-label="Envoyer"
                disabled={sendMessage.isPending || !draft.trim()}
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white transition hover:bg-brand-700 disabled:opacity-50"
              >
                <Send className="size-4" />
              </button>
            </form>
          )}

          {sendMessage.isError ? (
            <p className="px-4 pb-3 text-xs text-danger-500">Échec de l'envoi du message. Réessayez.</p>
          ) : null}
        </Card>
          )}
        </div>

        <HelpSidebar />
      </div>
    </div>
  );
}
